import type { TaxAnalysis } from '@popety-io/types'

// Re-export the TaxAnalysis type for convenience
export type { TaxAnalysis }

// Helper function to format tax burden as percentage
export const formatTaxBurden = (burden: number): string => {
  // Tax burden comes as decimal (e.g., 0.189 = 18.9%)
  return `${(burden * 100).toFixed(1)}%`
}

// Helper function to format CHF amount
export const formatCHF = (amount: number): string => {
  return `CHF ${amount.toLocaleString('de-CH', { maximumFractionDigits: 0 })}`
}

// Helper function to convert day number to date string
export const formatTaxFreedomDay = (dayNumber: number): string => {
  const date = new Date(2024, 0, 1)
  date.setDate(date.getDate() + dayNumber - 1)

  const day = date.getDate()
  const month = date.toLocaleDateString('en-US', { month: 'long' })

  return `${month} ${day}`
}

// Helper function to calculate percentage difference
export const calculatePercentageDifference = (
  value: number,
  reference: number
): number => {
  if (reference === 0) return 0
  return ((value - reference) / reference) * 100
}

// Helper function to get efficiency rating variant
export const getEfficiencyVariant = (
  rating: string
): 'high' | 'medium' | 'low' => {
  switch (rating) {
    case 'high':
      return 'high'
    case 'medium':
      return 'medium'
    case 'low':
      return 'low'
    default:
      return 'medium'
  }
}

// Helper function to calculate average metrics across profiles
export const calculateAverages = (data: TaxAnalysis) => {
  const { current_data } = data

  const avgTaxBurden =
    (current_data.tax_burden_single +
      current_data.tax_burden_married +
      current_data.tax_burden_family) /
    3

  const avgTotalTax =
    (current_data.total_tax_single +
      current_data.total_tax_married +
      current_data.total_tax_family) /
    3

  const avgTaxFreedomDay = Math.round(
    (current_data.tax_freedom_day_single +
      current_data.tax_freedom_day_married +
      current_data.tax_freedom_day_family) /
      3
  )

  return {
    avgTaxBurden,
    avgTotalTax,
    avgTaxFreedomDay,
  }
}

// Helper to get evolution data for display
export const getEvolutionDisplay = (
  evolution: number | undefined
): { value: string; isPositive: boolean } => {
  if (evolution === undefined || evolution === null) {
    return { value: 'N/A', isPositive: false }
  }

  const percentage = evolution * 100
  const isPositive = evolution <= 0 // Lower taxes = positive for the user
  const sign = evolution > 0 ? '+' : ''

  return {
    value: `${sign}${percentage.toFixed(1)}%`,
    isPositive,
  }
}
